import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const fmt = (v) => (Number(v) || 0).toLocaleString("en-US");

const AeroTable3 = ({ apiData = [] }) => {
  const pdfRef = useRef(null);

  const computed = useMemo(() => {
    if (!apiData || apiData.length === 0) return null;

    const rawYears = Object.keys(apiData[0] || {}).filter((k) =>
      /^\d{4}$/.test(k)
    );
    const years = rawYears
      .filter((y) => Number(y) <= 2024)
      .sort((a, b) => Number(a) - Number(b));

    const trendData = apiData[0] || {};

    const values = years.map((y) => Number(trendData[y]) || 0);

    const initialEmployment = values[0] || 0;
    const currentEmployment = values[values.length - 1] || 0;

    const growthPercent =
      initialEmployment > 0
        ? (
            ((currentEmployment - initialEmployment) / initialEmployment) *
            100
          ).toFixed(1)
        : "0.0";

    // Peak year and value
    let maxEmployment = -Infinity;
    let peakYear = years[0] || "2018";
    years.forEach((y) => {
      const v = Number(trendData[y]) || 0;
      if (v > maxEmployment) {
        maxEmployment = v;
        peakYear = y;
      }
    });

    // Best YoY growth year
    let maxGrowth = -Infinity;
    let bestGrowthYear = years[1] || years[0] || "2018";
    for (let i = 1; i < years.length; i++) {
      const curY = years[i];
      const prevY = years[i - 1];
      const curV = Number(trendData[curY]) || 0;
      const prevV = Number(trendData[prevY]) || 0;
      const g = prevV > 0 ? ((curV - prevV) / prevV) * 100 : 0;

      if (g > maxGrowth) {
        maxGrowth = g;
        bestGrowthYear = curY;
      }
    }

    return {
      years,
      trendData,
      values,
      initialEmployment,
      currentEmployment,
      growthPercent,
      maxEmployment,
      peakYear,
      maxGrowth,
      bestGrowthYear,
    };
  }, [apiData]);

  const chartData = useMemo(() => {
    if (!computed) return { labels: [], datasets: [] };

    return {
      labels: computed.years,
      datasets: [
        {
          label: "All Sexes Employment",
          data: computed.values,
          backgroundColor: "#ffffbb",
          borderColor: "#ffffbb",
          borderWidth: 1,
          hoverBackgroundColor: "#ffffbb",
        },
      ],
    };
  }, [computed]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      title: {
        display: true,
        text: "📊 Employment Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `Employment: ${fmt(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        grid: { display: false },
        ticks: { font: { size: window.innerWidth < 768 ? 10 : 12 } },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Employees",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        ticks: {
          font: { size: window.innerWidth < 768 ? 10 : 12 },
          callback: (v) => fmt(v),
        },
        grid: { display: true },
      },
    },
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    if (!input) return;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, pageWidth - 10, 35);
      };

      const addFooter = (pageNo) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNo}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);

      // More pages
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final branding footer block (same as your other pages)
      pdf.line(10, 260, pageWidth - 10, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment-trend-dashboard.pdf");
    });
  };

  if (!apiData || apiData.length === 0) {
    return <p>No data available for the employment trend.</p>;
  }

  if (!computed) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend (Yearly)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Growth Since {computed.years[0]}
            </p>
            <p className="text-xl font-bold text-blue-800">
              {Number(computed.growthPercent) >= 0 ? "+" : ""}
              {computed.growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Employment Year
            </p>
            <p className="text-xl font-bold text-yellow-800">
              {computed.peakYear}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Peak: {fmt(computed.maxEmployment)}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">2024 Employment</p>
            <p className="text-xl font-bold text-green-800">
              {fmt(computed.currentEmployment)} 👥
            </p>
          </div>
        </div>

        {/* Additional Metrics */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Best Growth Year (YoY)
            </p>
            <p className="text-xl font-bold text-purple-800">
              {computed.bestGrowthYear} ({computed.maxGrowth >= 0 ? "+" : ""}
              {computed.maxGrowth.toFixed(1)}%)
            </p>
          </div>

          <div className="p-4 bg-indigo-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Starting Employment
            </p>
            <p className="text-xl font-bold text-indigo-800">
              {fmt(computed.initialEmployment)}
            </p>
          </div>
        </div> */}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      All Sexes Employment
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {computed.years.map((y) => (
                    <tr
                      key={y}
                      className={`hover:bg-gray-50 ${
                        y === "2024"
                          ? "bg-green-100 font-bold"
                          : computed.years.indexOf(y) % 2 === 0
                            ? "bg-white"
                            : "bg-gray-50/60"
                      }`}
                    >
                      <td className="p-2 text-left">{y}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(computed.trendData[y])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Chart bars show total employment per year.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[420px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable3;
